import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {GtProducts} from '@app/models/enums/gt-products.enum';
import {GtLocalStorageService} from '@app/services/gt-localstorage.service';
import {GtWindowService} from '@app/services/gt-window.service';
import {environment} from '@environment';
import {AuthenticationService, LoginDto, LoginResponse, LoginTokenDto, Subscription, TokenResponse, UserData} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import {BehaviorSubject, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, finalize, first, map, tap} from 'rxjs/operators';

export interface AccessRights {
  read?: boolean;
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  super?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GtAuthenticationService {
  static SKIP_INTERCEPTOR = 'Skip-Interceptor';
  userData: UserData;
  login: string;
  oname: string;
  manageOname: string;
  sessionId: string;
  organizationAccessRights: AccessRights = {read: false, add: false, edit: false, delete: false};
  endUserAccessRights: AccessRights = {read: false, add: false, edit: false, delete: false};
  serviceAccessRights: AccessRights = {read: false, add: false, edit: false, delete: false};
  serviceProviderAccessRights: AccessRights = {read: false, add: false, edit: false, delete: false};

  private isloggedInSubject = new BehaviorSubject<boolean>(false);
  private subscriptionSubject = new ReplaySubject<Subscription[]>(1);

  constructor(
    private authenticationService: AuthenticationService,
    private localStorage: GtLocalStorageService,
    private windowService: GtWindowService,
    private translateService: TranslateService,
    private router: Router
  ) {
    // removed for fixing issue with gtHasOneOf directive
    // this.subscriptionSubject.next([])

    const hasStoredUserData = this.parseAndSetUserData();
    if (hasStoredUserData) {
      console.log('Already logged in');
      this.getSubscriptions().subscribe();
      this.loggedIn = true;
    }
  }

  set loggedIn(isloggedIn: boolean) {
    this.isloggedInSubject.next(isloggedIn);
  }

  get loggedIn() {
    return this.isloggedInSubject.value;
  }

  isloggedIn(): Observable<boolean> {
    return this.isloggedInSubject.asObservable();
  }

  hasTrigging(): Observable<boolean> {
    if (environment.requireTrigging) {
      return this.hasOneOf(GtProducts.TRIGGERING_PRODUCT);
    }
    return of(true);
  }

  doTokenLogin(token: string): Observable<LoginResponse> {
    const dto: LoginTokenDto = {
      appId: environment.appId,
      token
    };

    return new Observable(observer => {
      this.authenticationService.defaultHeaders = this.authenticationService.defaultHeaders.set(
        GtAuthenticationService.SKIP_INTERCEPTOR,
        ''
      );

      this.authenticationService
        .loginWithTokenDto1(dto)
        .pipe(
          finalize(() => {
            this.authenticationService.defaultHeaders = this.authenticationService.defaultHeaders.delete(
              GtAuthenticationService.SKIP_INTERCEPTOR
            );
          })
        )
        .subscribe(
          response => {
            if (response.code === 200) {
              this.sessionId = response.sessionId;
              this.localStorage.setItem('user', JSON.stringify(response.userData));
              this.setUserData(response.userData);
              this.loggedIn = true;
              this.getSubscriptions().subscribe(() => {
                observer.next(response);
                observer.complete();
              });
            } else {
              return observer.error(this.translateService.instant('login.failed'));
            }
          },
          e => observer.error(e.message)
        );
    });
  }

  doLogin(username: string, password: string): Observable<LoginResponse> {
    const dto: LoginDto = {
      appId: environment.appId,
      login: username,
      password
    };

    return new Observable(observer => {
      this.authenticationService.defaultHeaders = this.authenticationService.defaultHeaders.set(
        GtAuthenticationService.SKIP_INTERCEPTOR,
        ''
      );
      this.authenticationService
        .loginWithDto1(dto)
        .pipe(
          finalize(() => {
            this.authenticationService.defaultHeaders = this.authenticationService.defaultHeaders.delete(
              GtAuthenticationService.SKIP_INTERCEPTOR
            );
          })
        )
        .subscribe(
          response => {
            if (response.code === 200) {
              this.sessionId = response.sessionId;
              this.localStorage.setItem('user', JSON.stringify(response.userData));
              this.setUserData(response.userData);
              this.loggedIn = true;
              this.getSubscriptions().subscribe(() => {
                observer.next(response);
                observer.complete();
              });
            } else {
              return observer.error(this.translateService.instant('login.failed'));
            }
          },
          e => observer.error(e.message)
        );
    });
  }

  getToken(): Observable<TokenResponse> {
    return this.authenticationService.getToken1();
  }

  private parseAndSetUserData(): boolean {
    let userData: UserData;
    try {
      userData = JSON.parse(this.localStorage.getItem('user'));
      if (userData) {
        this.setUserData(userData);
        return true;
      }
    } catch (e) {
      console.log('Error parsing userData');
    }
    return false;
  }

  private setUserData(userData: UserData) {
    this.userData = userData;
    this.login = userData.login;
    this.oname = userData.organization;

    if (environment.enableSentry) {
      Sentry.setUser({
        organization: userData.organization,
        username: userData.login,
        email: userData.email || ''
      });
    }
  }

  getSubscriptions(): Observable<Subscription[]> {
    return this.authenticationService.getSubscriptions1().pipe(
      map(response => {
        const subscriptions = response.subscriptions || [];
        const serviceProviderSubscription: Subscription = subscriptions.find(s => s.product.id === GtProducts.VPN_SERVICE_PROVIDER_ROLE);
        if (serviceProviderSubscription) {
          const attributes = serviceProviderSubscription.attributes;
          this.updateElementAccessRights(this.organizationAccessRights, attributes.OrganizationRights);
          this.updateElementAccessRights(this.endUserAccessRights, attributes.EndUserRights);
          this.updateElementAccessRights(this.serviceAccessRights, attributes.ServiceRights);
          this.updateElementAccessRights(this.serviceProviderAccessRights, attributes.ServiceProviderRights);
          this.subscriptionSubject.next(subscriptions);
          return subscriptions;
        }
        const administratorSubscription: Subscription = subscriptions.find(s => s.product.id === GtProducts.VPN_ADMINISTRATOR_ROLE);
        if (administratorSubscription) {
          // TODO use administrator subscription attributes once they are available from the backend
          // const attributes = administratorSubscription.attributes;
          this.updateElementAccessRights(this.organizationAccessRights, 8);
          this.updateElementAccessRights(this.endUserAccessRights, 8);
          this.updateElementAccessRights(this.serviceAccessRights, 8);
          this.updateElementAccessRights(this.serviceProviderAccessRights, 0);
          this.subscriptionSubject.next(subscriptions);
          return subscriptions;
        } else {
          this.subscriptionSubject.next(subscriptions);
          return subscriptions;
        }
      })
    );
  }

  updateElementAccessRights(element: AccessRights, assignedRights: number) {
    const accessRightsBits = assignedRights.toString(2);
    try {
      element.read = accessRightsBits[0] === '1';
      element.add = accessRightsBits[1] === '1';
      element.edit = accessRightsBits[2] === '1';
      element.delete = accessRightsBits[3] === '1';
      element.super = accessRightsBits[4] === '1';
    } catch (err) {
      console.log('Error reading access rights: ' + err.message);
    }
  }

  doLogout(): Observable<void> {
    if (this.loggedIn) {
      return this.authenticationService.logout1().pipe(
        tap(response => {
          if (response.code !== 200) {
            console.log('Logout failed', response);
          }
        }),
        map(_ => {}),
        catchError(error => {
          console.log('Logout failed', error);
          return of(undefined);
        }),
        finalize(() => {
          this.localStorage.removeItem('user');
          this.loggedIn = false;
          this.subscriptionSubject.next([]);
          this.goToLogin();
        })
      );
    } else {
      return of(undefined).pipe(tap(() => this.goToLogin(false)));
    }
  }

  goToLogin(reload = true) {
    this.router
      .navigate(['login'])
      .then(() => {
        if (reload) {
          this.windowService.reload();
        }
      })
      .catch(err => {
        console.error('navigation failed', err);
      });
  }

  resetPassword(username: string): Observable<void> {
    const dto: LoginDto = {
      appId: environment.appId,
      login: username
    };
    return this.authenticationService.resetPassword1(dto).pipe(
      map(response => {
        if (response.code !== 200) {
          throw Error(this.translateService.instant('login.reset_password.failure'));
        }
      })
    );
  }

  hasOneOf(...products: number[]): Observable<boolean> {
    return this.subscriptionSubject.pipe(
      first(),
      map(subscriptions => subscriptions.some(s => products.some(id => s.product.id === id)))
    );
  }

  isAdmin(): Observable<boolean> {
    return this.hasOneOf(GtProducts.VPN_ADMINISTRATOR_ROLE, GtProducts.VPN_SERVICE_PROVIDER_ROLE);
  }

  hasAllOf(...products: number[]): Observable<boolean> {
    return this.subscriptionSubject.pipe(
      first(),
      map(subscriptions => products.every(id => subscriptions.some(s => s.product.id === id)))
    );
  }
}
